import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export const artSrc = (src = '') => {
    if (src.startsWith('/images/products/')) return `/studio-products/${src.split('/').pop().split('?')[0]}.webp`;
    return src.startsWith('/images/')
        ? src.replace('/images/', '/studio/').replace(/\.(jpe?g|png)(\?.*)?$/i, '.webp') : src;
};

export function StudioImage({ src = '', alt = '', sizes = '(max-width: 640px) 100vw, (max-width: 1000px) 50vw, 33vw', ...props }) {
    const full = artSrc(src);
    const uploaded = full.startsWith('/studio-products/');
    const optimized = uploaded || full.startsWith('/studio/');
    const variant = width => uploaded ? `${full}?w=${width}` : full.replace(/\.webp$/, `-${width}.webp`);
    return <img {...props} alt={alt} src={full} decoding="async" sizes={optimized ? sizes : undefined}
        srcSet={optimized ? `${variant(320)} 320w, ${variant(640)} 640w, ${full} 1400w` : undefined} />;
}

export function Arrow({ diagonal = false, ...props }) {
    return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}><path d={diagonal ? 'M5 19 19 5M5 5h14v14' : 'M4 12h16m-6-6 6 6-6 6'} stroke="currentColor" strokeWidth="1.3" /></svg>;
}
export function Icon({ name, ...props }) {
    const paths = {
        search: 'M21 21l-5-5M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0',
        bag: 'M5 7h14l1 14H4L5 7Zm4 0V5a3 3 0 0 1 6 0v2',
        user: 'M4 21a8 8 0 0 1 16 0M17 7a5 5 0 1 1-10 0 5 5 0 0 1 10 0',
        close: 'm5 5 14 14M19 5 5 19',
        menu: 'M3 8h18M3 16h18',
    };
    return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}><path d={paths[name]} stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}
export function Star({ className = '' }) {
    return <svg className={className} viewBox="0 0 100 100" aria-hidden="true"><g fill="none" stroke="currentColor" strokeWidth="1.2">{Array.from({ length: 12 }, (_, i) => <path key={i} d="M50 8 53 39 50 50 47 39Z" transform={`rotate(${i * 30} 50 50)`} />)}</g></svg>;
}

export function MotionPage({ children }) {
    const ref = useRef(null);
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
        if (!window.IntersectionObserver || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
        ref.current.classList.add('ds-motion');
        const observer = new IntersectionObserver(entries => entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('is-revealed');
                observer.unobserve(entry.target);
            }
        }), { threshold: 0.08 });
        ref.current.querySelectorAll('[data-reveal]').forEach(node => observer.observe(node));
        return () => observer.disconnect();
    }, [pathname]);
    return <main id="main-content" ref={ref} key={pathname} className="ds-main">{children}</main>;
}

export function PageTitle({ eyebrow, children, text }) {
    return <header className="ds-page-title" data-reveal><div><p className="ds-eyebrow">{eyebrow}</p><h1>{children}</h1></div>{text && <p className="ds-page-description">{text}</p>}</header>;
}

export function usePageTitle(title) {
    useEffect(() => { document.title = `${title} — Delaney Stewart`; }, [title]);
}
